import { FormFieldInput } from '../../../../ui/gzForm/formField/component/FormFieldInput';
import { css, html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { GzClearableInputWebComponent } from '../../../../ui/gzInput';
import { ButtonSize, FontSize } from '../../../../ui/gzIconButton/types';
import { CustomRuleValidator } from '../common/validators';
import { GzInputErrorWebComponent } from '../../../../ui/gzForm/inputError';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzIconButtonWebComponent } from '../../../../ui/gzIconButton';

export interface DirectoryPathItem {
  path: string;
  includeSubdirectories: boolean;
}

@customElement('gz-integrity-monitor-directory-path-form-field')
export class IntegrityMonitorDirectoryPathFormFieldWebComponent extends FormFieldInput<DirectoryPathItem[]> {
  @property({ type: Array })
  value: DirectoryPathItem[] = [];

  @property({ type: String })
  placeholder = '';

  @property({ type: Number })
  maxValues = 32;

  @property({ type: String })
  checkboxLabel = '';

  @property({ type: String })
  currentOs = '';

  @query('gz-input')
  public inputComponent: GzClearableInputWebComponent;

  @query('gz-checkbox')
  public checkboxComponent: GzCheckboxWebComponent;

  @query('gz-input-error')
  public inputError: GzInputErrorWebComponent;

  @query('gz-icon-button')
  public buttonComponent: GzIconButtonWebComponent;

  public validator: CustomRuleValidator<string> | null = null;

  public translations: any;

  public getFormValue(): DirectoryPathItem[] {
    return this.value;
  }

  public setFormValue(value: DirectoryPathItem[]): void {
    this.value = value;
    this.clearInputComponents();
    this.emitChangeEvent();
  }

  public markAsInvalid(): void {
    super.markAsInvalid();
  }

  public markAsValid(): void {
    super.markAsValid();

    if (this.inputComponent) this.inputComponent.markAsValid();
    if (this.inputError) this.inputError.Error = null;
  }

  public removeInvalid(): void {
    if (!this.validator) return;

    if (this.value.find(item => this.validator && !this.validator(item.path).valid) !== undefined) {
      this.value = this.value.filter(item => this.validator && this.validator(item.path).valid);
      this.emitChangeEvent();
    }
  }

  static get styles() {
    return css`
      .wrapper {
        display: flex;
        align-items: center;
      }

      #chip-container {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        cursor: default;
        width: 700px;
        margin-top: 8px;

        &.disabled {
          pointer-events: none;
        }
      }

      .mr {
        margin-right: 16px;
      }

      #checkbox {
        margin-top: 8px;
      }
    `;
  }

  private renderItems(item: DirectoryPathItem, index: number): TemplateResult {
    return html` <gz-chip
      text=${item.path + (item.includeSubdirectories ? ' (all)' : '')}
      notSelectable
      removable
      ?disabled="${this.disabled}"
      @removeChip="${() => this.handleRemoveItem(index)}"
    ></gz-chip>`;
  }

  render() {
    return html`
      <div class="wrapper"">
        <gz-input
          class="mr"
          placeholder="${this.placeholder}"
          inputwidth="240"
          @change="${this.handleChange}"
          @blur="${e => e.stopPropagation()}"
          ?disabled="${this.disabled}"
        ></gz-input>
        <gz-icon-button
          buttontype="square"
          buttonfill="primary"
          icon="ds-icon-add"
          size="${ButtonSize.SIZE_32}"
          fontSize="${FontSize.ICON_SIZE_20}"
          disabled
          @click="${this.handleClick}"
        ></gz-icon-button>
      </div>
      <gz-input-error></gz-input-error>
      <gz-checkbox
        class="mr"
        id="checkbox"
        color="primary"
        label="${this.checkboxLabel}"
        @blur="${e => e.stopPropagation()}"
        ?disabled="${this.disabled}"
      ></gz-checkbox>
      <div id="chip-container" class="${this.disabled ? 'disabled' : ''}">${this.value.map((chip, index) => this.renderItems(chip, index))}</div>
    `;
  }

  public setCurrentOs(os: string) {
    this.currentOs = os;
    this.inputError.Error = null;
    this.inputComponent.markAsValid();
    this.handleChange();
  }

  private handleChange() {
    this.buttonComponent.disabled =
      this.currentOs === '' || this.inputComponent.value.length === 0 || this.value.length >= this.maxValues;
  }

  private handleClick(e: Event) {
    const input = this.inputComponent.value;
    if (input.length === 0) return;

    if (this.value.find(item => item.path === input)) {
      this.inputError.Error = this.translations.errorDuplicateValueIntroduced;
      this.inputComponent.markAsInvalid();
      return;
    }

    if (input.endsWith('/') || input.endsWith('\\')) {
      this.inputError.Error = this.translations.errorDirectoryRuleEndingSlash;
      this.inputComponent.markAsInvalid();
      return;
    }

    if (this.validator) {
      const res = this.validator(input);
      if (!res.valid) {
        this.inputError.Error = res.error;
        this.inputComponent.markAsInvalid();
        return;
      }
    }

    const newItems = this.value.slice();
    newItems.push({
      path: input,
      includeSubdirectories: this.checkboxComponent.checked,
    });
    this.value = newItems;

    this.clearInputComponents();

    this.inputError.Error = null;
    this.inputComponent.markAsValid();

    this.checkboxComponent.checked = false;

    this.emitBlurEvent();
    this.emitChangeEvent();
  }

  private handleRemoveItem(index: number) {
    const newItems = this.value.slice();
    newItems.splice(index, 1);
    this.value = newItems;

    this.buttonComponent.disabled = this.inputComponent.value.length === 0 || this.value.length >= this.maxValues;

    this.emitBlurEvent();
    this.emitChangeEvent();
  }

  private emitBlurEvent() {
    const event = new CustomEvent('blur', {
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    this.dispatchEvent(event);
  }

  private emitChangeEvent() {
    const event = new CustomEvent('change', {
      detail: {
        value: this.value,
      },
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    this.dispatchEvent(event);
  }

  private clearInputComponents() {
    this.inputComponent.clearInputValue(new Event('clear'));
    this.checkboxComponent.setFormValue(false);
  }
}
